import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Link, router } from '@inertiajs/react'
import {
  Edit,
  Trash2,
  BookOpen,
  Calendar,
  Eye,
  EyeOff,
  Clock,
  User,
  FolderOpen,
  CheckCircle,
  ExternalLink
} from 'lucide-react'

interface ArticleDetail {
  id: number
  slug: string
  title: string
  excerpt: string | null
  body: string
  author_id: number
  author_name: string
  category_id: number
  category_name: string | null
  category_slug: string | null
  community_id: number
  community_name: string | null
  views_count: number
  reading_time: number
  published: boolean
  draft: boolean
  published_at: string | null
  created_at: string
  updated_at: string
}

interface Stats {
  views_count: number
  reading_time: number
}

interface ShowProps {
  article: ArticleDetail
  stats: Stats
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function Show({ article, stats }: ShowProps) {
  const handleDelete = () => {
    if (confirm(`Voulez-vous vraiment supprimer l'article "${article.title}" ?`)) {
      router.delete(`/admin/articles/${article.slug}`)
    }
  }

  const handlePublish = () => {
    router.post(`/admin/articles/${article.slug}/publish`)
  }

  const handleUnpublish = () => {
    router.post(`/admin/articles/${article.slug}/unpublish`)
  }

  const isScheduled = article.published_at && new Date(article.published_at) > new Date()

  return (
    <AdminLayout
      title={article.title}
      breadcrumbs={[
        { label: 'Articles', href: '/admin/articles' },
        { label: article.title }
      ]}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Article Header */}
          <Card className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-tessan-cream rounded-xl flex items-center justify-center">
                  <BookOpen size={28} className="text-tessan-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-text-dark">{article.title}</h2>
                  <p className="text-sm text-text-gray">/{article.slug}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Link href={`/admin/articles/${article.slug}/edit`}>
                  <Button variant="secondary" className="flex items-center gap-2">
                    <Edit size={16} />
                    Modifier
                  </Button>
                </Link>
                <Button
                  variant="secondary"
                  className="flex items-center gap-2 text-red-600 hover:bg-red-50"
                  onClick={handleDelete}
                >
                  <Trash2 size={16} />
                  Supprimer
                </Button>
              </div>
            </div>

            {/* Status Badge */}
            <div className="mb-6">
              {article.draft && (
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
                  <EyeOff size={16} />
                  Brouillon - Non visible
                </span>
              )}
              {article.published && (
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-700">
                  <CheckCircle size={16} />
                  Publie
                </span>
              )}
              {isScheduled && (
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700">
                  <Clock size={16} />
                  Programme pour le {formatDate(article.published_at!)}
                </span>
              )}
            </div>

            {/* Excerpt */}
            {article.excerpt && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-text-gray mb-2">Extrait</h3>
                <p className="text-text-dark bg-gray-50 p-4 rounded-lg italic">
                  {article.excerpt}
                </p>
              </div>
            )}

            {/* Metadata Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <User size={18} className="text-text-gray" />
                <div>
                  <p className="text-sm text-text-gray">Auteur</p>
                  <p className="text-text-dark font-medium">{article.author_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <FolderOpen size={18} className="text-text-gray" />
                <div>
                  <p className="text-sm text-text-gray">Categorie</p>
                  <p className="text-text-dark font-medium">{article.category_name || '-'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Eye size={18} className="text-text-gray" />
                <div>
                  <p className="text-sm text-text-gray">Vues</p>
                  <p className="text-text-dark font-medium">{stats.views_count}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock size={18} className="text-text-gray" />
                <div>
                  <p className="text-sm text-text-gray">Temps de lecture</p>
                  <p className="text-text-dark font-medium">{stats.reading_time} min</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Article Content Preview */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-dark">Contenu</h3>
              {article.published && article.category_slug && (
                <Link
                  href={`/content/${article.category_slug}/${article.slug}`}
                  className="text-tessan-primary hover:underline text-sm flex items-center gap-1"
                >
                  Voir sur le site
                  <ExternalLink size={14} />
                </Link>
              )}
            </div>

            <div className="prose prose-sm max-w-none bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-text-dark font-sans">
                {article.body}
              </pre>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Publication Actions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Publication</h3>
            <div className="space-y-3">
              {article.draft && (
                <Button
                  onClick={handlePublish}
                  className="w-full justify-center gap-2"
                >
                  <Eye size={16} />
                  Publier maintenant
                </Button>
              )}
              {(article.published || isScheduled) && (
                <Button
                  variant="secondary"
                  onClick={handleUnpublish}
                  className="w-full justify-center gap-2"
                >
                  <EyeOff size={16} />
                  Depublier
                </Button>
              )}
              <Link href={`/admin/articles/${article.slug}/edit`} className="block">
                <Button variant="secondary" className="w-full justify-start gap-2">
                  <Edit size={16} />
                  Modifier l'article
                </Button>
              </Link>
            </div>
          </Card>

          {/* Stats Card */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Statistiques</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Eye size={20} className="text-blue-600" />
                  <span className="text-text-dark">Vues</span>
                </div>
                <span className="text-xl font-bold text-blue-600">{stats.views_count}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock size={20} className="text-green-600" />
                  <span className="text-text-dark">Temps de lecture</span>
                </div>
                <span className="text-xl font-bold text-green-600">{stats.reading_time} min</span>
              </div>
            </div>
          </Card>

          {/* Info Card */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Informations</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-text-gray">
                <Calendar size={16} />
                <span>Cree le {formatDate(article.created_at)}</span>
              </div>
              <div className="flex items-center gap-2 text-text-gray">
                <Calendar size={16} />
                <span>Modifie le {formatDate(article.updated_at)}</span>
              </div>
              {article.published_at && (
                <div className="flex items-center gap-2 text-text-gray">
                  <Eye size={16} />
                  <span>
                    {article.published ? 'Publie' : 'Programme'} le {formatDate(article.published_at)}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-text-gray">
                <FolderOpen size={16} />
                <span>Communaute: {article.community_name}</span>
              </div>
            </div>
          </Card>

          {/* Actions Card */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-dark mb-4">Actions</h3>
            <div className="space-y-2">
              <Link href="/admin/articles" className="block">
                <Button variant="secondary" className="w-full justify-start gap-2">
                  <BookOpen size={16} />
                  Retour a la liste
                </Button>
              </Link>
              <Link href="/admin/articles/new" className="block">
                <Button variant="secondary" className="w-full justify-start gap-2">
                  <BookOpen size={16} />
                  Nouvel article
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}
